<template>
  <div id="app">
    <router-view v-if="isAuthPage" />

    <el-container v-else class="app-shell">
      <el-aside width="220px" class="app-aside">
        <div class="brand">
          <div class="brand-mark">七</div>
          <div>
            <div class="brand-title">小七系统</div>
            <div class="brand-subtitle">分析 · 账户 · 任务</div>
          </div>
        </div>

        <el-menu
          :default-active="$route.path"
          class="side-menu"
          background-color="#ffffff"
          text-color="#526071"
          active-text-color="#2563eb"
          router
        >
          <el-menu-item index="/dashboard"><i class="el-icon-s-home"></i><span slot="title">首页</span></el-menu-item>
          <el-menu-item index="/first-book"><i class="el-icon-notebook-2"></i><span slot="title">第一本账</span></el-menu-item>
          <el-menu-item index="/simulation-account"><i class="el-icon-wallet"></i><span slot="title">模拟账户</span></el-menu-item>
          <el-menu-item index="/real-positions"><i class="el-icon-document"></i><span slot="title">真实持仓</span></el-menu-item>
          <el-menu-item index="/core-index-history"><i class="el-icon-data-line"></i><span slot="title">核心指数</span></el-menu-item>
          <el-menu-item index="/history-monitor"><i class="el-icon-monitor"></i><span slot="title">数据健康</span></el-menu-item>
          <el-menu-item index="/scheduled-tasks"><i class="el-icon-timer"></i><span slot="title">任务监控</span></el-menu-item>
          <el-menu-item index="/scheduled-task-runs"><i class="el-icon-finished"></i><span slot="title">运行记录</span></el-menu-item>
          <el-menu-item index="/conclusions"><i class="el-icon-document-checked"></i><span slot="title">结论留痕</span></el-menu-item>
          <el-menu-item index="/system-settings"><i class="el-icon-setting"></i><span slot="title">系统设置</span></el-menu-item>
        </el-menu>

        <div class="aside-note">
          <strong>页面原则</strong>
          <span>普通页面只展示业务状态和分析可用性；内部运维细节由后台治理，不作为日常操作界面。</span>
        </div>
      </el-aside>

      <el-container class="app-main">
        <el-header class="app-header">
          <div class="header-title-block">
            <h2>{{ pageTitle }}</h2>
            <p>{{ pageDesc }}</p>
          </div>
          <div class="header-actions">
            <div class="time-pill">
              <i class="el-icon-date"></i>
              <div><strong>{{ headerDate }}</strong><span>{{ headerWeek }} · {{ headerTime }} 北京时间</span></div>
            </div>
            <el-button size="small" icon="el-icon-refresh" class="refresh-btn" :loading="refreshing" @click="refreshPage">刷新页面</el-button>
            <el-tag size="small" effect="plain">{{ frontendVersion }}</el-tag>
            <el-tag :type="backendTagType" size="small" effect="plain">后端：{{ backendStatus }}</el-tag>
            <div v-if="loggedIn" class="user-pill">
              <div class="user-avatar">{{ userInitial }}</div>
              <span>{{ userName }}</span>
              <el-button type="text" size="mini" @click="logout">退出</el-button>
            </div>
            <el-button v-else size="small" type="primary" class="login-entry" @click="goLogin">登录</el-button>
          </div>
        </el-header>

        <el-main class="app-content">
          <router-view :key="routeViewKey" ref="pageView" />
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script>
import { systemSettingsApi } from '@/api'
import { authApi } from '@/api/auth'
import { clearAuth, getUser, isLoggedIn } from '@/utils/auth'

const beijingClockFormatter = new Intl.DateTimeFormat('zh-CN', {
  timeZone: 'Asia/Shanghai',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
})

export default {
  name: 'AppPhase5',
  data() {
    return {
      frontendVersion: 'UI · 分析健康版',
      backendStatus: '检测中',
      headerDate: '',
      headerWeek: '',
      headerTime: '',
      clockTimer: null,
      refreshing: false,
      routeViewKey: 0,
      loggedIn: false,
      user: null
    }
  },
  computed: {
    isAuthPage() { return this.$route.path === '/login' },
    pageTitle() { return (this.$route.meta && this.$route.meta.title) || '小七系统' },
    pageDesc() { return (this.$route.meta && this.$route.meta.desc) || '查看分析数据、账户和任务状态。' },
    backendTagType() {
      if (this.backendStatus === '在线') return 'success'
      if (this.backendStatus === '离线') return 'danger'
      return 'info'
    },
    userName() { return (this.user && (this.user.name || this.user.username || this.user.account)) || '管理员' },
    userInitial() { return String(this.userName || '管').slice(0, 1) }
  },
  watch: {
    '$route.path'() { this.syncAuthState() }
  },
  mounted() {
    this.syncAuthState()
    this.updateHeaderTime()
    this.checkBackend()
    this.clockTimer = setInterval(this.updateHeaderTime, 1000)
  },
  beforeDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer)
  },
  methods: {
    syncAuthState() {
      this.loggedIn = isLoggedIn()
      this.user = getUser()
    },
    updateHeaderTime() {
      const parts = beijingClockFormatter.formatToParts(new Date()).reduce((result, item) => {
        result[item.type] = item.value
        return result
      }, {})
      this.headerDate = `${parts.year}-${parts.month}-${parts.day}`
      this.headerWeek = parts.weekday
      this.headerTime = `${parts.hour}:${parts.minute}:${parts.second}`
    },
    async checkBackend() {
      try {
        await systemSettingsApi.healthCheck()
        this.backendStatus = '在线'
      } catch (error) {
        this.backendStatus = '离线'
      }
    },
    async refreshPage() {
      this.refreshing = true
      try {
        await this.checkBackend()
        const page = this.$refs.pageView
        if (page && typeof page.refreshData === 'function') {
          await page.refreshData()
        } else {
          this.routeViewKey += 1
        }
        this.$message.success('页面已刷新')
      } catch (error) {
        this.routeViewKey += 1
        this.$message.warning('已重新加载页面区域')
      } finally {
        this.refreshing = false
      }
    },
    goLogin() { this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } }) },
    async logout() {
      try { await authApi.logout() } catch (error) { /* 本地退出不被后端登出失败阻断。 */ }
      clearAuth()
      this.syncAuthState()
      this.$message.success('已退出登录')
      this.$router.replace('/login')
    }
  }
}
</script>

<style>
html, body, #app { height: 100%; margin: 0; }
body { overflow: hidden; background: #eef3f9; }
#app { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', Arial, sans-serif; color: #111827; }
.app-shell { min-height: 100vh; background: #eef3f9; }
.app-aside { position: fixed; inset: 0 auto 0 0; z-index: 1000; width: 220px !important; background: #fff; border-right: 1px solid #dfe7f2; overflow-y: auto; overflow-x: hidden; }
.brand { height: 72px; display: flex; align-items: center; padding: 0 16px; border-bottom: 1px solid #edf2f8; }
.brand-mark { width: 38px; height: 38px; border-radius: 13px; display: flex; align-items: center; justify-content: center; margin-right: 10px; background: linear-gradient(135deg, #1d4ed8, #60a5fa); color: #fff; font-weight: 800; }
.brand-title { color: #0f172a; font-size: 16px; font-weight: 800; }
.brand-subtitle { margin-top: 3px; color: #7f8ea3; font-size: 12px; }
.side-menu { border-right: none; padding: 10px 12px 16px; }
.el-menu-item { height: 44px; line-height: 44px; margin: 4px 0; border-radius: 11px; }
.el-menu-item i { margin-right: 10px; color: #64748b; }
.el-menu-item.is-active { background: #e8f1ff !important; color: #1d4ed8 !important; font-weight: 700; box-shadow: inset 3px 0 0 #2563eb; }
.el-menu-item.is-active i { color: #2563eb; }
.aside-note { margin: 18px 14px; padding: 13px; border-radius: 12px; background: #f3f7fc; border: 1px solid #e2eaf4; }
.aside-note strong, .aside-note span { display: block; }
.aside-note strong { color: #334155; font-size: 12px; }
.aside-note span { margin-top: 4px; color: #64748b; font-size: 12px; line-height: 1.6; }
.app-main { min-width: 0; margin-left: 220px; min-height: 100vh; width: calc(100vw - 220px); }
.app-header { position: sticky; top: 0; z-index: 900; height: 72px !important; padding: 0 20px; background: rgba(248, 251, 255, 0.97); border-bottom: 1px solid #dfe7f2; display: flex; align-items: center; justify-content: space-between; gap: 18px; }
.header-title-block { min-width: 220px; }
.app-header h2 { margin: 0; color: #0f172a; font-size: 20px; font-weight: 800; }
.app-header p { margin: 4px 0 0; color: #64748b; font-size: 13px; }
.header-actions { display: flex; align-items: center; gap: 9px; }
.time-pill { min-width: 200px; height: 40px; display: flex; align-items: center; gap: 9px; padding: 0 11px; border-radius: 12px; background: #fff; border: 1px solid #dce6f2; }
.time-pill i { color: #2563eb; font-size: 17px; }
.time-pill strong, .time-pill span { display: block; }
.time-pill strong { color: #0f172a; font-size: 13px; line-height: 15px; }
.time-pill span { margin-top: 2px; color: #64748b; font-size: 11px; line-height: 13px; }
.refresh-btn, .login-entry { border-radius: 10px; }
.user-pill { height: 36px; display: flex; align-items: center; gap: 7px; padding: 0 9px; border-radius: 12px; background: #fff; border: 1px solid #dce6f2; color: #334155; font-size: 13px; }
.user-avatar { width: 24px; height: 24px; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 700; background: #2563eb; }
.app-content { height: calc(100vh - 72px); overflow: auto; padding: 18px 22px 26px; }
.el-card { border-color: #dfe7f2; }
.el-card__header { border-bottom-color: #e8eef6; }
@media (max-width: 1180px) { .time-pill, .header-actions > .el-tag { display: none; } }
</style>
